import React from 'react';
import {
  addDaysYmd,
  calendarDayOfWeekFromYmd,
  isPrivateSlotBlockedOnCalendarDate,
  isSessionStartAtLeastHoursAhead,
  type CoachAvailabilityException,
} from '@court-sync/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatTimeHHMM } from '../../../utils/timeFormat';
import { calendarPrivateSlotKey, isPrivateSlotWithinFreeWindows, privateSlotKey } from './privateBookingScheduleUtils';

const DAY_LABELS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Weekly template slot; each column applies it to that column’s calendar date. */
export type WeekTemplateSlot = { dayOfWeek: number; startTime: string; endTime: string };

export type CalendarSessionSlot = WeekTemplateSlot & { sessionDate: string };

type Props = {
  /** YYYY-MM-DD (local) — past calendar days cannot be booked. */
  todayYmd: string;
  /** Minimum hours from now before a session can start (academy setting). */
  minLeadHours: number;
  weekStartMondayYmd: string;
  onWeekOffset: (deltaWeeks: number) => void;
  /** Inclusive YYYY-MM-DD bounds for navigation (e.g. today … +18 months). */
  minWeekStartMondayYmd: string;
  maxWeekStartMondayYmd: string;
  /** Session-sized steps within the coach’s weekly availability template; each is enabled only if it also lies in `freeWindows` and passes exceptions (below). */
  slots: WeekTemplateSlot[];
  /** Free time after subtracting recurring lesson blocks — used with the template to enable vs gray out each slot. */
  freeWindows: { dayOfWeek: number; startTime: string; endTime: string }[];
  exceptions: CoachAvailabilityException[];
  selectedKeys: string[];
  onToggleSlot: (slot: CalendarSessionSlot) => void;
  formatDayDate: (ymd: string) => string;
};

/** Monday + 0..6 → YYYY-MM-DD */
function dayYmd(weekStartMondayYmd: string, index: number): string {
  return addDaysYmd(weekStartMondayYmd, index);
}

export function PrivateBookingWeekCalendar({
  todayYmd,
  minLeadHours,
  weekStartMondayYmd,
  onWeekOffset,
  minWeekStartMondayYmd,
  maxWeekStartMondayYmd,
  slots,
  freeWindows,
  exceptions,
  selectedKeys,
  onToggleSlot,
  formatDayDate,
}: Props) {
  const canPrev = weekStartMondayYmd > minWeekStartMondayYmd;
  const canNext = weekStartMondayYmd < maxWeekStartMondayYmd;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Tap dates and times to book. Sessions must start at least {minLeadHours} hour
          {minLeadHours === 1 ? '' : 's'} from now. Gray slots are unavailable (other lessons, schedule gaps, or too
          soon). Amber slots are blocked on that date (exception).
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canPrev}
            onClick={() => onWeekOffset(-1)}
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium tabular-nums min-w-[10rem] text-center">
            {formatDayDate(dayYmd(weekStartMondayYmd, 0))} – {formatDayDate(dayYmd(weekStartMondayYmd, 6))}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canNext}
            onClick={() => onWeekOffset(1)}
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
        {Array.from({ length: 7 }, (_, i) => {
          const dateYmd = dayYmd(weekStartMondayYmd, i);
          const dow = calendarDayOfWeekFromYmd(dateYmd);
          const isPastDay = dateYmd < todayYmd;

          const slotsForDay = slots.filter((slot) => slot.dayOfWeek === dow);

          return (
            <div
              key={dateYmd}
              className="rounded-lg border border-border bg-card p-3 min-h-[120px] flex flex-col"
            >
              <div className="mb-2 text-center border-b border-border/60 pb-2">
                <div className="text-xs font-medium text-muted-foreground">{DAY_LABELS_SHORT[dow]}</div>
                <div className="text-sm font-semibold text-foreground">{formatDayDate(dateYmd)}</div>
              </div>
              <div className="flex flex-col gap-1.5 flex-1">
                {isPastDay ? (
                  <p className="text-xs text-muted-foreground text-center py-2">Past date</p>
                ) : slotsForDay.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-2">No slots</p>
                ) : (
                  slotsForDay.map((slot) => {
                    const templateKey = privateSlotKey(slot);
                    const sessionSlot: CalendarSessionSlot = {
                      ...slot,
                      sessionDate: dateYmd,
                    };
                    const k = calendarPrivateSlotKey(dateYmd, slot.startTime, slot.endTime);
                    const selected = selectedKeys.includes(k);
                    const inFreeWindow = isPrivateSlotWithinFreeWindows(slot, freeWindows);
                    const exceptionBlocked = isPrivateSlotBlockedOnCalendarDate(
                      dateYmd,
                      slot.startTime,
                      slot.endTime,
                      exceptions
                    );
                    const tooSoon = !isSessionStartAtLeastHoursAhead(dateYmd, slot.startTime, minLeadHours);
                    const canSelect = inFreeWindow && !exceptionBlocked && !tooSoon;

                    if (canSelect) {
                      return (
                        <button
                          key={`${dateYmd}-${templateKey}`}
                          type="button"
                          onClick={() => onToggleSlot(sessionSlot)}
                          className={cn(
                            'rounded-md border px-2 py-1.5 text-xs font-medium transition-colors text-left',
                            selected
                              ? 'border-primary bg-primary/15 text-foreground'
                              : 'border-input bg-background hover:bg-muted/50'
                          )}
                        >
                          {formatTimeHHMM(slot.startTime)} – {formatTimeHHMM(slot.endTime)}
                        </button>
                      );
                    }

                    const busyOverlap = !inFreeWindow;
                    return (
                      <button
                        key={`${dateYmd}-${templateKey}`}
                        type="button"
                        disabled
                        title={
                          tooSoon
                            ? `Book at least ${minLeadHours} hour${minLeadHours === 1 ? '' : 's'} from now`
                            : exceptionBlocked
                              ? 'Not available on this date (coach exception)'
                              : busyOverlap
                                ? 'Not available — overlaps another lesson or does not fit in open time for this session'
                                : 'Unavailable'
                        }
                        className={cn(
                          'rounded-md border px-2 py-1.5 text-xs font-medium text-left cursor-not-allowed opacity-90',
                          exceptionBlocked
                            ? 'border-amber-300/80 bg-amber-50 text-amber-950 dark:border-amber-700/80 dark:bg-amber-950/40 dark:text-amber-100'
                            : 'border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400',
                          selected && exceptionBlocked && 'ring-2 ring-primary/35'
                        )}
                      >
                        {formatTimeHHMM(slot.startTime)} – {formatTimeHHMM(slot.endTime)}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Monday 00:00 local of the week containing `ymd`. */
export function mondayOfWeekContainingYmd(ymd: string): string {
  const dow = calendarDayOfWeekFromYmd(ymd);
  const offset = dow === 0 ? -6 : 1 - dow;
  return addDaysYmd(ymd, offset);
}
